import React, { useState } from 'react';
import {
  Button, Menu, MenuItem, ListItemIcon, ListItemText, Divider,
  Typography, Box,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

/**
 * Toolbar "Actions ▾" dropdown. Groups items by `group` (insertion order
 * preserved) with a header label and dividers between groups.
 *
 * Props:
 *   label            — button label (default "Actions")
 *   actions          — [{ key, label, icon?, onClick, disabled?, group?, dangerous? }]
 *   selectedCount    — when >0, button shows "Actions (N)" and any action with
 *                      requiresSelection:true is enabled only when count > 0
 *   variant          — MUI Button variant (default "outlined")
 *   color            — MUI Button color (default "primary")
 *   size             — MUI Button size (default "medium")
 *
 * Each action's onClick receives no args — the parent already knows which
 * rows are selected via its own state.
 */
export default function EntityActionsMenu({
  label = 'Actions',
  actions = [],
  selectedCount = 0,
  variant = 'outlined',
  color = 'primary',
  size = 'medium',
  buttonProps = {},
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  // Group preservation: walk actions in order, collecting per-group lists.
  const groups = [];
  const groupIndex = new Map();
  for (const a of actions) {
    const g = a.group || 'Actions';
    if (!groupIndex.has(g)) {
      groupIndex.set(g, groups.length);
      groups.push({ name: g, items: [] });
    }
    groups[groupIndex.get(g)].items.push(a);
  }

  return (
    <>
      <Button
        variant={variant}
        color={color}
        size={size}
        onClick={handleClick}
        endIcon={<KeyboardArrowDownIcon />}
        {...buttonProps}
      >
        {label}{selectedCount > 0 ? ` (${selectedCount})` : ''}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            minWidth: 240,
            mt: 0.5,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            border: '1px solid',
            borderColor: 'divider',
          },
        }}
      >
        {groups.map((g, gi) => (
          <Box key={g.name}>
            {gi > 0 && <Divider sx={{ my: 0.5 }} />}
            <Typography
              variant="caption"
              sx={{
                px: 2, py: 0.5, display: 'block',
                color: 'text.secondary', fontWeight: 600,
                textTransform: 'uppercase', letterSpacing: 0.5,
              }}
            >
              {g.name}
            </Typography>
            {g.items.map((a) => {
              const needsSelection = a.requiresSelection === true;
              const disabled = a.disabled || (needsSelection && selectedCount === 0);
              return (
                <MenuItem
                  key={a.key || a.label}
                  disabled={disabled}
                  onClick={() => {
                    handleClose();
                    a.onClick?.();
                  }}
                  sx={{
                    py: 1,
                    color: a.dangerous ? 'error.main' : 'inherit',
                    '&:hover': a.dangerous
                      ? { bgcolor: 'error.lighter', color: 'error.dark' }
                      : undefined,
                  }}
                >
                  {a.icon && (
                    <ListItemIcon sx={{ color: 'inherit', minWidth: 32 }}>
                      {a.icon}
                    </ListItemIcon>
                  )}
                  <ListItemText
                    primary={a.label}
                    secondary={
                      needsSelection && selectedCount === 0
                        ? 'Select rows to enable'
                        : a.hint
                    }
                    primaryTypographyProps={{ fontSize: 14 }}
                    secondaryTypographyProps={{ fontSize: 11 }}
                  />
                </MenuItem>
              );
            })}
          </Box>
        ))}
      </Menu>
    </>
  );
}
